import { Stack, Divider, TextField, CircularProgress, InputAdornment, Typography, Button, Popover, MenuItem, MenuList } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FC, useMemo, useRef, useState } from "react";
import SearchCriteriaChips, { ChipItem } from "@/components/input/SearchCriteriaChips";
import { useUsersTableSearchContext } from "./UsersTableSearchProvider";
import { ReduxState } from "ra-core";
import { useSelector } from "react-redux";
import { RoleResponseDto, UserStatus } from "@/gen/api/user";
import { useUserManageAmbientDataContext } from "./UserManageAmbientDataContextProvider";
import { PopBaseFilter } from "@/components/input/PopAutocompleteFilter";
import { SimpleOption } from "@/components/governance/search/AmbientFacilitiesContext";


const UserTableSearchRegion: FC = () => {
  const {
    states,
    setSearchRole,
    setSearchStatus,
  } = useUsersTableSearchContext()

  const criteriaChips = useMemo(() => {
    const arr = [] as ChipItem[][]
    if (states.roles.length > 0) {
      arr.push(states.roles.map(r => ({
        id: `c_${r.isDefault ? 0 : 1}${r.roleId}`,
        label: 'Role',
        content: r.name!,
        delete: () => setSearchRole(r, true)
      })))
    }
    if (states.status != null) {
      arr.push([{
        id: 'status',
        label: 'Status',
        content: states.status === UserStatus.Active ? 'Active' : 'Inactive',
        delete: () => setSearchStatus(undefined),
      }])
    }
    return arr
  }, [setSearchRole, setSearchStatus, states])

  return (
    <>
      <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ p: 2, pl: 4, m: -1}}>
        <SearchNameField />
        <PopRoleFilter />
        <PopStatusFilter />
      </Stack>
      <Divider />
      <SearchCriteriaChips criteriaChips={criteriaChips} />
    </>
  )
}

export default UserTableSearchRegion;


const SearchNameField : FC = () => {
  const loading = useSelector<ReduxState, boolean>(store => !!store.admin.loading)
  const {
    states: {name},
    searchName,
  } = useUsersTableSearchContext()
  return (
    <TextField 
      id="userstable-search-name"
      placeholder='Search by Name'
      fullWidth
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {loading ? <CircularProgress size={20} /> : <SearchIcon fontSize="small" />}
          </InputAdornment>
        )
      }}
      onChange={(e) => searchName(e.target.value)}
      value={name}
      variant="outlined"
      sx={{ width: 280, mr: 'auto' }}
    />
  )
}


const PopRoleFilter: FC = () => {
  const {roles} = useUserManageAmbientDataContext()
  const { setSearchRole } = useUsersTableSearchContext()
  return (
    <PopBaseFilter 
      label="Role"
      id='user-role-picker'
      options={roles}
      onChange={(event, option) => {
        const rItem = option as RoleResponseDto
        if (rItem) {
          setSearchRole(rItem, false)
        }
      }}
    />
  )
}

const statusOptions : SimpleOption[] = [
  { key: UserStatus.Active, text: 'Active' },
  { key: UserStatus.Inactive, text: 'Inactive' },
  { key: '--' as any, text: <Typography variant="caption" color="InactiveCaptionText">both</Typography> as any },
]
const PopStatusFilter: FC = () => {
  const { states, setSearchStatus } = useUsersTableSearchContext()

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const handleOpenMenu = (): void => {
    setOpenMenu(true);
  }

  const handleCloseMenu = (): void => {
    setOpenMenu(false);
  };
  return (
    <>
      <Button
        color="inherit"
        endIcon={<ExpandMoreIcon fontSize="small" />}
        onClick={handleOpenMenu}
        ref={anchorRef}
      >
        Status
      </Button>
      {openMenu &&
        <Popover
          anchorEl={anchorRef.current}
          open={openMenu}
          onClose={handleCloseMenu}
          PaperProps={{ sx: {width: 80 } }}
        >
          <MenuList id="split-button-menu" autoFocusItem>
            {statusOptions.map((option, index) => (
              <MenuItem
                key={option.key}
                selected={option.key === states.status}
                onClick={(event) => {
                  setSearchStatus(
                    option.key === ('--' as any) ? undefined : option.key
                  )
                  handleCloseMenu()
                }}
              >
                {option.text}
              </MenuItem>
            ))}
          </MenuList>
        </Popover>}
    </>
  )
}



